import { billingApi } from "./billingApi";
import { BillingCode, SubscriptionStatus, describeBillingError } from "./billingCodes";
import { getStripe } from "./stripeClient";

// The subscription statuses that owe nothing: a trial, or a paid period.
const PAID_UP_STATUSES = new Set([SubscriptionStatus.TRIALING, SubscriptionStatus.ACTIVE]);

// The backend learns that a check failed from Stripe's invoice.payment_failed
// webhook, and until that arrives billing-status still asks for the very check
// that just failed, with the same secret. It is read again after these waits,
// so the caller is handed what the status is about to say (usually: add a
// card) rather than a check that can no longer pass.
const STALE_CHECK_DELAYS_MS = [1000, 1500, 2000, 2500, 3000];

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** True while a status still asks for the check this secret belongs to. */
const asksForCheck = (status, clientSecret) =>
   Boolean(status?.requiresAction) && status.actionClientSecret === clientSecret;

/**
 * True when a status leaves nothing for the cardholder's bank to settle: no
 * payment waiting on a check, and nothing unpaid.
 */
export const isPaymentSettled = (status) =>
   Boolean(status) && !status.requiresAction && PAID_UP_STATUSES.has(status.subscriptionStatus);

/**
 * Finish what a BillingStatus asks the cardholder for.
 *
 * `requiresAction` is not a failure: the bank wants 3-D Secure on a payment —
 * the invoice a resumed subscription opens, a renewal that was held. Stripe runs
 * that challenge in the browser (`handleNextAction`) from the status's
 * `actionClientSecret`, and afterwards the status is read again, because only
 * the server knows whether the invoice then went through.
 *
 * `actionClientSecret` is only filled in by GET billing-status, which reads
 * Stripe fresh, so a status that asks for action without one is re-read first.
 * If there is still no secret, or the browser has no handleNextAction, the
 * status's `hostedInvoiceUrl` — Stripe's own payment page — is the way through,
 * and the caller is told to offer it.
 *
 * `error` is the bank turning the payment down, and it is only ever set while
 * the payment is still owed. It is a refusal, not a delay: Stripe has put the
 * payment back to requires_payment_method, and nothing more happens on its own.
 * A caller given one never waits for access and never announces it; it shows
 * the refusal and offers a way round (billingActions.js, resolveFixOutcome).
 * The status handed back with it has had a few seconds to catch up with the
 * refusal, which the backend hears of from a webhook.
 *
 * @returns {Promise<{status: Object, error: Object|null, needsHostedPage: boolean}>}
 */
export async function settlePaymentAction(status, organizationId) {
   const { bankError, ...settled } = await runPaymentAction(status, organizationId);
   return settled;
}

/**
 * Finish the bank's check on an upgrade's difference invoice — the
 * requiresAction a change-plan answered — and hand back a refusal when the plan
 * did not move.
 *
 * settlePaymentAction counts the bank's "no" only while the payment is still
 * owed. The difference invoice of an upgrade is voided once its payment fails,
 * though, so afterwards nothing is owed and the status reads as paid although
 * the plan never changed. Here the bank's "no" counts unless the status holds
 * the new plan (`holdsPlan`), read again for a few seconds, because the switch
 * reaches the backend by webhook as well.
 *
 * @returns {Promise<{status: Object, error: Object|null, needsHostedPage: boolean}>}
 */
export async function settlePlanChangePayment(status, organizationId, holdsPlan) {
   const { bankError, ...settled } = await runPaymentAction(status, organizationId);
   if (settled.error || !bankError) return settled;

   let fresh = settled.status;

   for (const delay of STALE_CHECK_DELAYS_MS) {
      if (holdsPlan(fresh)) return { ...settled, status: fresh };
      await wait(delay);
      fresh = await billingApi.getBillingStatus(organizationId);
   }

   return holdsPlan(fresh)
      ? { ...settled, status: fresh }
      : { status: fresh, error: bankError, needsHostedPage: false };
}

/**
 * The bank's check a status is waiting on, run from the payment notice.
 *
 * On an active subscription the only payment that can wait on the bank is an
 * upgrade's difference invoice: a renewal or a resumed subscription that needs
 * the check is past_due. That one is settled as settlePlanChangePayment settles
 * it — refused unless the plan moved — and `isPlanChange` says so, for the
 * caller to add that the plan hasn't changed. Anything else is
 * settlePaymentAction's.
 *
 * @returns {Promise<{status: Object, error: Object|null, needsHostedPage: boolean, isPlanChange: boolean}>}
 */
export async function settleWaitingPayment(status, organizationId) {
   if (status?.subscriptionStatus !== SubscriptionStatus.ACTIVE) {
      return { ...(await settlePaymentAction(status, organizationId)), isPlanChange: false };
   }

   // New Enterprise terms keep the packet, and only the terms in force move: a
   // different `termsId` is a change of them.
   const moved = (answer) =>
      Boolean(answer) &&
      (answer.packetId !== status.packetId ||
         answer.isMonthly !== status.isMonthly ||
         answer.enterprisePlan?.termsId !== status.enterprisePlan?.termsId);

   return { ...(await settlePlanChangePayment(status, organizationId, moved)), isPlanChange: true };
}

// settlePaymentAction, also keeping what the bank answered (`bankError`) when
// the status says the payment went through anyway.
async function runPaymentAction(status, organizationId) {
   if (!status?.requiresAction) {
      return { status, error: null, needsHostedPage: false, bankError: null };
   }

   let current = status;

   if (!current.actionClientSecret) {
      current = await billingApi.getBillingStatus(organizationId);
   }

   if (!current?.requiresAction) {
      return { status: current, error: null, needsHostedPage: false, bankError: null };
   }

   const stripe = current.actionClientSecret ? await getStripe() : null;

   if (typeof stripe?.handleNextAction !== "function") {
      return {
         status: current,
         error: null,
         needsHostedPage: Boolean(current.hostedInvoiceUrl),
         bankError: null
      };
   }

   const result = await stripe.handleNextAction({ clientSecret: current.actionClientSecret });

   // Read back whatever the bank said: a challenge that was abandoned can still
   // leave a payment that went through, and the other way round. So what the
   // bank reported only counts as a refusal while the payment is still owed.
   let fresh = await billingApi.getBillingStatus(organizationId);

   if (result?.error) {
      for (const delay of STALE_CHECK_DELAYS_MS) {
         if (!asksForCheck(fresh, current.actionClientSecret)) break;
         await wait(delay);
         fresh = await billingApi.getBillingStatus(organizationId);
      }
   }

   const refused = Boolean(result?.error) && !isPaymentSettled(fresh);

   return {
      status: fresh,
      error: refused ? result.error : null,
      // Stripe's own page is the way round a check that could not open here.
      // A refused one did open, and the caller says so instead.
      needsHostedPage: !refused && Boolean(fresh?.requiresAction && fresh.hostedInvoiceUrl),
      bankError: result?.error ?? null
   };
}

/**
 * A payment the bank declined, as the billing endpoints answer it — 409
 * PAYMENT_FAILED with the status it left — in the shape settlePaymentAction
 * hands back: that status, and the bank's reason as the `error`. Null for any
 * other failure, which is the caller's to report.
 */
export const readDecline = (failure) =>
   failure?.code === BillingCode.PAYMENT_FAILED && failure.status
      ? {
           status: failure.status,
           error: {
              message: failure.status.paymentFailureMessage || describeBillingError(failure)
           },
           needsHostedPage: false
        }
      : null;

// Several overdue invoices can each want the bank's check (an annual plan whose
// card failed for months). One check per invoice, at most a year of them.
const MAX_PAYMENT_ROUNDS = 12;

/**
 * Finish the payment a status started, all of it.
 *
 * Like settlePaymentAction, and then: an annual plan owes every overdue month,
 * and one bank's check only settles the invoice it was for. So once a check has
 * gone through and billing-status still reports something owed (`amountDue`),
 * the rest is paid with the same card (pay-outstanding), which can ask for
 * another check, and so on. The amount the cardholder confirmed was all of it.
 *
 * A decline on the way is handed back as settlePaymentAction hands back a
 * refusal. Any other failure is thrown.
 *
 * @returns {Promise<{status: Object, error: Object|null, needsHostedPage: boolean}>}
 */
export async function settlePayment(status, organizationId) {
   let settled = await settlePaymentAction(status, organizationId);
   let checked = Boolean(status?.requiresAction);

   for (let round = 0; round < MAX_PAYMENT_ROUNDS; round += 1) {
      if (!checked || settled.error || settled.needsHostedPage || !settled.status?.amountDue) break;

      let next;
      try {
         next = await billingApi.payOutstanding({ organizationId });
      } catch (failure) {
         const decline = readDecline(failure);
         if (decline) return decline;
         throw failure;
      }

      checked = Boolean(next?.requiresAction);
      settled = await settlePaymentAction(next, organizationId);
   }

   return settled;
}
